import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signUp } from '../services/sign-up';
import { useAuth } from '../auth/AuthContext';
import { getIpInfo } from '../services/api';
import { useDispatch } from 'react-redux';
import { setProjectDetails } from '../redux/slices/projectSlice';
import { validateInvitation } from '../services/invitationApi';
import { AuthLayout } from '../components/layout/AuthLayout';
import { Loading } from '../components/ui/Spinner';
import { AuthTextField } from '../components/auth/AuthTextField';
import { AuthPasswordField } from '../components/auth/AuthPasswordField';
import { AuthInviteBadges } from '../components/auth/AuthInviteBadges';
import { AuthPolicyBlock } from '../components/auth/AuthPolicyBlock';
import { AuthSubmitButton } from '../components/auth/AuthSubmitButton';
import { AuthCardFooter } from '../components/auth/AuthCardFooter';
import {
  isPasswordPolicyCompliant,
  PASSWORD_POLICY_ERROR_MESSAGE,
  PASSWORD_POLICY_HINT,
} from '../utils/passwordPolicy';

const PUBLIC_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];

function getInviteErrorMessage(errorCode?: string) {
  switch (errorCode) {
    case 'Expired':
      return 'This invitation has expired.';
    case 'Consumed':
      return 'This invitation has already been used.';
    case 'Revoked':
      return 'This invitation is no longer valid.';
    default:
      return 'This invitation link is not valid.';
  }
}

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [projectName, setProjectName] = useState('');
  const [inviteRoleName, setInviteRoleName] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [isOpenSignup, setIsOpenSignup] = useState(false);
  const [isValidatingInvite, setIsValidatingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { login } = useAuth();

  useEffect(() => {
    const token = searchParams.get('invite');
    if (!token) {
      setIsOpenSignup(true);
      setIsValidatingInvite(false);
      return;
    }

    setInviteToken(token);

    let redirectedToSignIn = false;

    validateInvitation(token)
      .then((result) => {
        if (!result.isValid) {
          setInviteError(getInviteErrorMessage(result.errorCode));
          return;
        }

        if (result.accountExists) {
          redirectedToSignIn = true;
          navigate(`/signin?invite=${encodeURIComponent(token)}`, { replace: true });
          return;
        }

        setProjectName(result.projectDisplayName || '');
        setInviteRoleName(result.roleDisplayName || '');
        setIsEmailLocked(Boolean(result.isEmailLocked));
        setEmailHint(result.emailHint || '');
        if (result.isEmailLocked && result.emailAddress) {
          setEmail(result.emailAddress);
        }
      })
      .catch(() => {
        setInviteError('Unable to validate this invitation. Please try again later.');
      })
      .finally(() => {
        if (!redirectedToSignIn) {
          setIsValidatingInvite(false);
        }
      });
  }, [searchParams, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage('');

    if (!isOpenSignup && !inviteToken) {
      setErrorMessage('This invitation link is not valid.');
      return;
    }

    const emailDomain = email.split('@')[1];
    if (PUBLIC_EMAIL_DOMAINS.includes(emailDomain)) {
      setErrorMessage('Invalid email address. Please use your work email to sign up.');
      return;
    }

    if (!isPasswordPolicyCompliant(password)) {
      setErrorMessage(PASSWORD_POLICY_ERROR_MESSAGE);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await signUp({
        emailAddress: email,
        password,
        ...(inviteToken ? { inviteToken } : {}),
      });

      if (response.data.succeeded) {
        if (response.data.data.state.indexOf('PRF') !== -1) {
          navigate(`/profile/${response.data.data.userId}`, {
            state: { email, password },
          });
        } else {
          const { ipAddress, userAgent } = await getIpInfo();
          const user = await login(email, password, ipAddress, userAgent);
          const projectIdValue = user.projectDetails?.[0]?.projectId ?? 1;
          localStorage.setItem('projectId', String(projectIdValue));
          dispatch(setProjectDetails(user.projectDetails));
          navigate('/project');
        }
      } else {
        setErrorMessage(response.data.message);
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Error occurred during sign-up. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const signInHref = inviteToken
    ? `/signin?invite=${encodeURIComponent(inviteToken)}`
    : '/signin';

  const lockedEmail = isEmailLocked && (email || emailHint) ? email || emailHint : undefined;

  if (isValidatingInvite) {
    return (
      <AuthLayout>
        <Loading text="Validating invitation…" />
      </AuthLayout>
    );
  }

  if (inviteError) {
    return (
      <AuthLayout title="Invitation unavailable" subtitle="This link cannot be used to sign up.">
        <div className="auth-alert auth-alert--error" role="alert">
          {inviteError}
        </div>
        <Link to="/signin" className="auth-link">
          Return to login
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign up"
      subtitle={
        isOpenSignup
          ? 'Create your client account using your work email.'
          : inviteRoleName === 'SuperUser'
            ? 'Create your project manager account using your work email.'
            : 'Create your account using your work email.'
      }
    >
      <AuthInviteBadges
        projectName={projectName}
        roleName={!isOpenSignup ? inviteRoleName : undefined}
        lockedEmail={lockedEmail}
      />

      {errorMessage ? (
        <div className="auth-alert auth-alert--error" role="alert">
          {errorMessage}
          {inviteToken && /already exists/i.test(errorMessage) ? (
            <p className="auth-alert__action">
              <Link to={signInHref} className="auth-link">
                Login to join this project
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit}>
        <AuthTextField
          id="email"
          label="Work email"
          type="email"
          value={email}
          onChange={(event) => {
            if (!isEmailLocked) setEmail(event.target.value);
          }}
          placeholder="johndoe@company.com"
          autoComplete="email"
          readOnly={isEmailLocked}
          required
          hint={
            isEmailLocked
              ? 'Email is fixed for this invitation and cannot be changed.'
              : undefined
          }
        />

        <AuthPasswordField
          id="password"
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Enter your password"
          autoComplete="new-password"
          required
          hint={PASSWORD_POLICY_HINT}
        />

        <AuthPasswordField
          id="confirm-password"
          label="Confirm password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          placeholder="Confirm your password"
          autoComplete="new-password"
          required
        />

        <AuthSubmitButton loading={loading}>
          {loading ? 'Creating account…' : 'Sign up'}
        </AuthSubmitButton>
      </form>

      <AuthCardFooter>
        <p className="auth-footer-text">
          Already registered?{' '}
          <Link to={signInHref} className="auth-link">
            Login
          </Link>
        </p>

        {!inviteToken ? (
          <p className="auth-footer-text auth-footer-text--compact">
            Need a new company and project?{' '}
            <Link to="/request-project" className="auth-link">
              Request a project
            </Link>
          </p>
        ) : null}

        <AuthPolicyBlock />
      </AuthCardFooter>
    </AuthLayout>
  );
}

export default SignUp;
